import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useFeatureValue,
  useGrowthBook,
} from '@growthbook/growthbook-react';
import { preloadVideos } from '../utils/imagePreloader';
import { setLanderContent } from '../utils/analytics';

// Single JSON-typed feature flag that owns the trans lander's content config:
//   (Post-auth routing was moved OUT of this config into the standalone
//    `createaitrans-paywall-nav-config` flag — see utils/postSignupRedirect.js.)
//   - step1.{Asian|Caucasian}: ethnicity reveal videos. Capitalized keys
//       match option.value directly.
//   - step2/3/4.{asian|caucasian}.…: every later step branches on the Step 1
//       choice, so the asset palette feels coherent end-to-end. Lowercase
//       branch keys; lookups must normalize the selection via ethnicityKey().
//   - step4.{asian|caucasian}.haircut.{value} / .eye.{value}: optional videos
//       for the hairstyle + eye color picks (Step 4 used to be image-only).
//   - generateVideo.{Asian|Caucasian}: hero video shown on the Summary step.
//       While the "generating" timer runs we display its first frame only;
//       after it finishes the video starts playing.
//
// Defaults below double as the documented shape — this is what gets applied
// if the flag is missing or partially configured in GrowthBook. Empty
// strings mean "no GB override, use the hardcoded asset or image fallback".
const DEFAULT_CONFIG = {
  step1: { Asian: '', Caucasian: '' },
  step2: {
    asian: {
      Sporty: '',
      Goth: '',
      Artist: '',
      Student: '',
      Activist: '',
      Ladyboy: '',
      Soldier: '',
      Actress: '',
    },
    caucasian: {
      Sporty: '',
      Goth: '',
      Artist: '',
      Student: '',
      Activist: '',
      Ladyboy: '',
      Soldier: '',
      Actress: '',
    },
  },
  step3: {
    asian: { Slim: '', Athletic: '' },
    caucasian: { Slim: '', Athletic: '' },
  },
  step4: {
    asian: {
      haircut: {
        Straight: '',
        Bangs: '',
        Braids: '',
        Bun: '',
        Curly: '',
        Ponytail: '',
      },
      eye: { Blue: '', Brown: '' },
    },
    caucasian: {
      haircut: {
        Straight: '',
        Bangs: '',
        Braids: '',
        Bun: '',
        Curly: '',
        Ponytail: '',
      },
      eye: { Blue: '', Brown: '' },
    },
  },
  generateVideo: { Asian: '', Caucasian: '' },
};

const GrowthBookContext = createContext(null);

// Shallow merge with a default — so a partial GB payload still produces a
// fully-shaped object and downstream `obj.key` lookups never throw.
const mergeShape = (def, cfg) => ({ ...def, ...(cfg || {}) });

// Recursively flatten every string leaf for the preload pass.
const flattenUrls = (node) => {
  if (!node) return [];
  if (typeof node === 'string') return [node];
  if (typeof node !== 'object') return [];
  return Object.values(node).flatMap(flattenUrls);
};

export const GrowthBookProvider = ({ children }) => {
  const growthbook = useGrowthBook();
  const config = useFeatureValue('createaitrans-config', DEFAULT_CONFIG);
  // Separate ON/OFF string flag that gates the email-only magic-link auth
  // flow (no password; existing emails get an OTP modal). Lives outside the
  // JSON config so it can be flipped independently from content overrides.
  const magicLink = useFeatureValue('createaitrans-magicLink', 'OFF');
  console.log('[GB] createaitrans-magicLink raw value =', magicLink, typeof magicLink);
  // Post-auth navigation config — replaces the old config.postSignupFlow field.
  // JSON: { type: 'paywall' | 'chat' | 'chat_paywall' }.
  const paywallNavConfig = useFeatureValue('createaitrans-paywall-nav-config', { type: 'paywall' });

  // Gate step rendering on GB readiness so the user never sees the hardcoded
  // fallback videos flash before the GB-resolved ones swap in.
  // Safety net: 1.5s timeout — if GB never resolves (network blocked,
  // ad-blocker, etc.) we still render rather than stranding the user.
  const [isReady, setIsReady] = useState(growthbook?.ready ?? false);
  useEffect(() => {
    if (isReady) return;
    if (growthbook?.ready) {
      setIsReady(true);
      return;
    }
    const t = setTimeout(() => setIsReady(true), 1500);
    return () => clearTimeout(t);
  }, [growthbook?.ready, isReady]);

  const values = useMemo(() => {
    const cfg = config || DEFAULT_CONFIG;
    // Normalize the nav type so casing typos in the hand-edited GB JSON don't break the
    // redirect. Valid values: 'paywall' (default) | 'chat' | 'chat_paywall'.
    const rawType = (paywallNavConfig?.type || 'paywall').toString().toLowerCase();
    const navType =
      rawType === 'chat_paywall' ? 'chat_paywall' : rawType === 'chat' ? 'chat' : 'paywall';

    const step2 = {
      asian: mergeShape(DEFAULT_CONFIG.step2.asian, cfg.step2?.asian),
      caucasian: mergeShape(
        DEFAULT_CONFIG.step2.caucasian,
        cfg.step2?.caucasian,
      ),
    };
    const step3 = {
      asian: mergeShape(DEFAULT_CONFIG.step3.asian, cfg.step3?.asian),
      caucasian: mergeShape(
        DEFAULT_CONFIG.step3.caucasian,
        cfg.step3?.caucasian,
      ),
    };
    const step4 = {
      asian: {
        haircut: mergeShape(
          DEFAULT_CONFIG.step4.asian.haircut,
          cfg.step4?.asian?.haircut,
        ),
        eye: mergeShape(DEFAULT_CONFIG.step4.asian.eye, cfg.step4?.asian?.eye),
      },
      caucasian: {
        haircut: mergeShape(
          DEFAULT_CONFIG.step4.caucasian.haircut,
          cfg.step4?.caucasian?.haircut,
        ),
        eye: mergeShape(
          DEFAULT_CONFIG.step4.caucasian.eye,
          cfg.step4?.caucasian?.eye,
        ),
      },
    };

    return {
      isReady,
      navType,
      // Content-config `name` (e.g. "default"), forwarded as `lander-content` in
      // the ab_testing payload sent with auth requests — mirrors create-friend.
      landerContent: cfg.name,
      // Accept either the canonical string flag ('ON'/'OFF') or a boolean
      // flag (true/false) in GrowthBook — both normalize to 'ON'/'OFF' here
      // so downstream code only has to compare against one shape.
      magicLink:
        magicLink === 'ON' || magicLink === true || magicLink === 'on'
          ? 'ON'
          : 'OFF',
      videos: {
        step1: mergeShape(DEFAULT_CONFIG.step1, cfg.step1),
        step2,
        step3,
        step4,
        generateVideo: mergeShape(
          DEFAULT_CONFIG.generateVideo,
          cfg.generateVideo,
        ),
      },
    };
  }, [isReady, config, magicLink, paywallNavConfig]);

  // Forward the config's `name` to analytics as `lander-content`, so it rides
  // along on every event. No-ops until GB resolves a payload with `name`.
  useEffect(() => {
    if (config?.name) setLanderContent(config.name);
  }, [config?.name]);

  // Warm the browser cache for every video URL the flow could play, as soon
  // as GB has resolved — by the time the user navigates onto a step, the
  // video is already on disk and autoplay starts without a buffering gap.
  useEffect(() => {
    if (!isReady) return;
    const allUrls = flattenUrls(values.videos).filter((u) => u && u.trim());
    if (allUrls.length > 0) preloadVideos(allUrls);
  }, [isReady, values.videos]);

  return (
    <GrowthBookContext.Provider value={values}>
      {children}
    </GrowthBookContext.Provider>
  );
};

export const useGrowthBookValues = () => {
  const context = useContext(GrowthBookContext);
  if (!context) {
    throw new Error(
      'useGrowthBookValues must be used within GrowthBookProvider',
    );
  }
  return context;
};

// Map a Step 1 ethnicity selection ('Asian' / 'Caucasian') to the lowercase
// branch key used by step2/step3/step4 in the GB config. Defaults to 'asian'
// when no ethnicity is yet known so accidental early lookups still return a
// shaped object instead of undefined.
export const ethnicityKey = (ethnicity) =>
  (ethnicity || 'asian').toString().toLowerCase() === 'caucasian'
    ? 'caucasian'
    : 'asian';
